import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  API_BASE,
  apiRequest,
  assignees,
  buttonClass,
  inputClass,
  statuses,
  statusLabel,
} from "./shared";

const PAGE_SIZE = 10;

export default function AllLeads() {
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const requestId = useRef(0);

  const pageItems = useMemo(() => {
    const visible = new Set([1, 2, 3, totalPages - 2, totalPages - 1, totalPages]);
    if (page > 3 && page < totalPages - 2) {
      visible.add(page - 1);
      visible.add(page);
      visible.add(page + 1);
    }
    const numbers = [...visible].filter((number) => number >= 1 && number <= totalPages).sort((a, b) => a - b);
    return numbers.flatMap((number, index) =>
      index && number - numbers[index - 1] > 1
        ? ["ellipsis-" + number, number]
        : [number]
    );
  }, [page, totalPages]);

  const [loading, setLoading] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    level: "",
    assignedTo: "",
  });

  const [createForm, setCreateForm] =
    useState({
      name: "",
      email: "",
      phone: "",
      country: "",
      questionanswers: "",
    });

  /* ---------------------------------------------------------------------- */
  /*                              FETCH LEADS                               */
  /* ---------------------------------------------------------------------- */

  const fetchLeads = useCallback(async () => {
    const currentRequest = ++requestId.current;
    try {
      setLoading(true);
      setMessage("");

      const params = new URLSearchParams();

      params.set("page", String(page));
      params.set("limit", String(PAGE_SIZE));

      if (filters.search.trim()) {
        params.set(
          "search",
          filters.search.trim()
        );
      }

      if (filters.status) {
        params.set(
          "status",
          filters.status
        );
      }

      if (filters.level) {
        params.set(
          "level",
          filters.level
        );
      }

      if (filters.assignedTo) {
        params.set(
          "assignedTo",
          filters.assignedTo
        );
      }

      const result = await apiRequest(
        `${API_BASE}/leads?${params.toString()}`
      );

      if (currentRequest !== requestId.current) return;
      const items = Array.isArray(result.data) ? result.data : result.data?.leads || [];
      const meta = result.pagination || result.data?.pagination || {};
      const count = Number(meta.total ?? result.total ?? result.data?.total);
      const pages = Number(meta.totalPages ?? result.totalPages ?? result.data?.totalPages);
      setLeads(items);
      setTotal(Number.isFinite(count) ? count : items.length);
      setTotalPages(Number.isFinite(pages) && pages > 0
        ? pages : Number.isFinite(count) ? Math.max(1, Math.ceil(count / PAGE_SIZE)) : 1);
    } catch (error) {
      if (currentRequest === requestId.current) setMessage(error.message);
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [filters, page]);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  /* ---------------------------------------------------------------------- */
  /*                              CREATE LEAD                               */
  /* ---------------------------------------------------------------------- */

  const createLead = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      setMessage("");

      const questionanswers =
        createForm.questionanswers
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean);

      const result = await apiRequest(
        `${API_BASE}/create`,
        {
          method: "POST",

          body: JSON.stringify({
            name: createForm.name,
            email: createForm.email,
            phone: createForm.phone,
            country: createForm.country,
            questionanswers,
          }),
        }
      );

      setMessage(
        `Lead #${result.data.leadId} created and assigned to ${result.data.assignedTo}`
      );

      setCreateForm({
        name: "",
        email: "",
        phone: "",
        country: "",
        questionanswers: "",
      });

      await fetchLeads();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
        {/* Header */}

        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            Lead Management
          </p>

          <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
            All Leads
          </h1>

          <p className="mt-1 text-sm text-slate-500">
            Create, search and manage your leads.
          </p>
        </div>

        {/* Message */}

        {message && (
          <div className="mb-5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
            {message}
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[340px_1fr]">
          {/* ============================================================ */}
          {/* CREATE LEAD                                                  */}
          {/* ============================================================ */}

          <section className="h-fit rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="font-semibold">
              Create Lead
            </h2>

            <p className="mb-5 mt-1 text-xs text-slate-500">
              Lead ID and assignee are generated automatically.
            </p>

            <form
              onSubmit={createLead}
              className="space-y-3"
            >
              <input
                className={inputClass}
                placeholder="Name"
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />

              <input
                className={inputClass}
                placeholder="Email"
                type="email"
                value={createForm.email}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />

              <input
                className={inputClass}
                placeholder="Phone"
                value={createForm.phone}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
              />

              <input
                className={inputClass}
                placeholder="Country"
                value={createForm.country}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    country: e.target.value,
                  }))
                }
              />

              <textarea
                className={`${inputClass} min-h-28 resize-none`}
                placeholder={
                  "Question answers\nOne answer per line"
                }
                value={
                  createForm.questionanswers
                }
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    questionanswers:
                      e.target.value,
                  }))
                }
              />

              <button
                disabled={loading}
                className={`${buttonClass} w-full`}
              >
                {loading
                  ? "Creating..."
                  : "Create Lead"}
              </button>
            </form>
          </section>

          {/* ============================================================ */}
          {/* ALL LEADS                                                    */}
          {/* ============================================================ */}

          <section className="min-w-0">
            {/* Filters */}

            <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <input
                  className={inputClass}
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => updateFilter("search", e.target.value)}
                />

                <select
                  className={inputClass}
                  value={filters.status}
                  onChange={(e) => updateFilter("status", e.target.value)}
                >
                  <option value="">
                    All statuses
                  </option>

                  {statuses
                    .filter(Boolean)
                    .map((status) => (
                      <option
                        key={status}
                        value={status}
                      >
                        {statusLabel(status)}
                      </option>
                    ))}
                </select>

                <select
                  className={inputClass}
                  value={filters.level}
                  onChange={(e) => updateFilter("level", e.target.value)}
                >
                  <option value="">
                    All levels
                  </option>

                  <option value="level-1">
                    Level 1
                  </option>

                  <option value="level-2">
                    Level 2
                  </option>
                </select>

                <select
                  className={inputClass}
                  value={filters.assignedTo}
                  onChange={(e) => updateFilter("assignedTo", e.target.value)}
                >
                  <option value="">
                    All assignees
                  </option>

                  {assignees.map((person) => (
                    <option
                      key={person}
                      value={person}
                    >
                      {person}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* List */}

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <h2 className="font-semibold">
                  Leads
                </h2>

                <span className="text-xs text-slate-500">
                  {total} results
                </span>
              </div>

              {loading && !leads.length ? (
                <div className="p-10 text-center text-sm text-slate-500">
                  Loading...
                </div>
              ) : leads.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-500">
                  No leads found.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {leads.map((lead) => (
                    <button
                      key={lead._id}
                      type="button"
                      onClick={() =>
                        navigate(
                          `/manage-lead/${lead.leadId}`
                        )
                      }
                      className="grid w-full gap-3 px-5 py-4 text-left transition hover:bg-slate-50 sm:grid-cols-[70px_1fr_120px_130px_120px]"
                    >
                      <span className="text-sm font-semibold">
                        #{lead.leadId}
                      </span>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {lead.leadDetails
                            ?.name || "Unknown"}
                        </p>

                        <p className="truncate text-xs text-slate-500">
                          {lead.leadDetails
                            ?.phone ||
                            lead.leadDetails
                              ?.email ||
                            "—"}
                        </p>
                      </div>
                      
                      <div>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium">
                          {lead.level || "—"}
                        </span>
                      </div>

                      <div>

                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium">
                          {statusLabel(
                            lead.currentStatus
                          )}
                        </span>
                      </div>

                      <p className="text-xs text-slate-500">
                        {lead.assignedTo ||
                          "Unassigned"}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <nav aria-label="Lead pages" className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
                <button type="button" disabled={loading || page === 1} onClick={() => setPage((p) => p - 1)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40">
                  Previous
                </button>
                {pageItems.map((item) => typeof item === "number" ? (
                  <button key={item} type="button" disabled={loading} aria-label={`Page ${item}`}
                    aria-current={page === item ? "page" : undefined} onClick={() => setPage(item)}
                    className={`min-w-9 rounded-lg border px-2 py-2 text-sm disabled:opacity-50 ${page === item ? "border-purple-700 bg-purple-700 text-white" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                    {item}
                  </button>
                ) : <span key={item} aria-hidden="true" className="px-1 text-slate-400">…</span>)}
                <button type="button" disabled={loading || page === totalPages} onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40">
                  Next
                </button>
              </nav>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}